import { Button } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { ActionButton, type ActionTone, DialogModal, Flex, MoreActions, RowActions } from "@zfp-akademie-a-s/zfp-design";
import { CheckCircleIcon, DeleteIcon, EditIcon } from "@zfp-akademie-a-s/zfp-design/icons";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { InvestmentCaseContext } from "Components/Analyses/_Shared/InvestmentCaseContext";
import { ArchiveIcon, RestoreIcon } from "Components/_Shared/Icons/Icons";
import {
  getGetAnalysisGoalsQueryKey,
  getGetFamilyGoalQueryKey,
  useDeleteGoal,
  useUpdateGoalLifecycleStatus,
  useUpdateGoalProgressStatus,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import { type GoalOverview, GoalOverviewLifecycleStatus, GoalOverviewProgressStatus, GoalOverviewType } from "Infrastructure/ApiZfp/Schema";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { type ReactNode, useContext, useState } from "react";
import { toast } from "sonner";

export const GOAL_CARD_ACTIONS_CLASS = "goal-card-actions";

/** Room for all four of the package's 32px action buttons and RowActions' 4px gaps, so the header doesn't shift when they appear. */
const ACTIONS_SLOT_WIDTH = 4 * 32 + 3 * 4;

const Actions = Resources.AnalysisDetail.Goals.Timeline.Actions;

type GoalAction = {
  key: string;
  text: typeof Actions.Archive;
  icon: ReactNode;
  tone: ActionTone;
  /** A completed goal's toggle keeps the success tint, not only on hover. */
  isOn?: boolean;
  onClick: () => void;
};

type Props = {
  goal: GoalOverview;
  onEdit?: () => void;
  /** Tablets and phones get the actions in a menu, they have no room for the row of buttons nor hover to reveal it. */
  isTablet: boolean;
};

export const GoalCardActions = ({ goal, onEdit, isTablet }: Props) => {
  const { guid, goalId, type, name, progressStatus, lifecycleStatus } = goal;
  const { analysisId, versionId, readOnly: isAnalysisReadOnly } = useContext(AnalysisDetailContext);
  const { isInvestmentCaseLocked } = useContext(InvestmentCaseContext);
  const { t } = useResource();
  const queryClient = useQueryClient();
  const deleteGoalMutation = useDeleteGoal();
  const updateLifecycleMutation = useUpdateGoalLifecycleStatus();
  const updateProgressMutation = useUpdateGoalProgressStatus();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  if (isAnalysisReadOnly || isInvestmentCaseLocked) {
    return null;
  }

  const isCompleted = progressStatus === GoalOverviewProgressStatus.completed;
  const isArchived = lifecycleStatus === GoalOverviewLifecycleStatus.archived;
  const isPending = deleteGoalMutation.isPending || updateLifecycleMutation.isPending || updateProgressMutation.isPending;

  const invalidateGoals = () => queryClient.invalidateQueries({ queryKey: getGetAnalysisGoalsQueryKey(analysisId, versionId) });
  const showError = () => toast.error(t(Resources.AnalysisDetail.Toast.GoalError));

  const toggleCompleted = () =>
    updateProgressMutation.mutate(
      {
        analysisId,
        versionId,
        goalId: guid,
        progressStatus: isCompleted ? GoalOverviewProgressStatus.in_progress : GoalOverviewProgressStatus.completed,
      },
      { onSuccess: invalidateGoals, onError: showError },
    );

  const setLifecycleStatus = (lifecycleStatus: GoalOverviewLifecycleStatus) =>
    updateLifecycleMutation.mutate(
      { analysisId, versionId, goalId: guid, lifecycleStatus },
      { onSuccess: invalidateGoals, onError: showError },
    );

  const deleteGoal = () =>
    deleteGoalMutation.mutate(
      { analysisId, versionId, goalId: guid },
      {
        onSuccess: () => {
          invalidateGoals();
          if (type === GoalOverviewType.children) {
            queryClient.invalidateQueries({ queryKey: getGetFamilyGoalQueryKey(analysisId, versionId, goalId) });
          }
        },
        onError: showError,
        onSettled: () => setIsDeleteDialogOpen(false),
      },
    );

  const actions: (GoalAction | false | undefined)[] = [
    !isArchived && {
      key: "completed",
      text: isCompleted ? Actions.MarkNotCompleted : Actions.MarkCompleted,
      icon: <CheckCircleIcon />,
      tone: "success",
      isOn: isCompleted,
      onClick: toggleCompleted,
    },
    isArchived
      ? {
          key: "lifecycle",
          text: Actions.Unarchive,
          icon: <RestoreIcon />,
          tone: "default",
          onClick: () => setLifecycleStatus(GoalOverviewLifecycleStatus.active),
        }
      : {
          key: "lifecycle",
          text: Actions.Archive,
          icon: <ArchiveIcon />,
          tone: "default",
          onClick: () => setLifecycleStatus(GoalOverviewLifecycleStatus.archived),
        },
    onEdit && { key: "edit", text: Actions.Edit, icon: <EditIcon />, tone: "default", onClick: onEdit },
    { key: "delete", text: Actions.Delete, icon: <DeleteIcon />, tone: "danger", onClick: () => setIsDeleteDialogOpen(true) },
  ];
  const availableActions = actions.filter((action): action is GoalAction => !!action);

  return (
    // The actions sit inside the clickable card, so their clicks must not open the goal. React bubbles events
    // through portals too, so this also covers clicks inside the menu and the delete dialog.
    <Flex ml="auto" flexShrink={0} onClick={(event) => event.stopPropagation()}>
      {isTablet ? (
        <MoreActions
          label={t(Actions.More)}
          disabled={isPending}
          items={availableActions.map(({ text, icon, tone, onClick }) => ({ label: t(text.Title), icon, tone, onClick }))}
        />
      ) : (
        <Flex className={GOAL_CARD_ACTIONS_CLASS} width={ACTIONS_SLOT_WIDTH} justifyContent="flex-end">
          <RowActions>
            {availableActions.map(({ key, text, icon, tone, isOn, onClick }) => (
              <ActionButton
                key={key}
                label={t(text.Title)}
                description={t(text.Description)}
                icon={icon}
                tone={tone}
                disabled={isPending}
                onClick={onClick}
                sx={isOn ? { bgcolor: "success.light", color: "success.main" } : undefined}
              />
            ))}
          </RowActions>
        </Flex>
      )}

      <DialogModal
        size="sm"
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        isPending={deleteGoalMutation.isPending}
        closeLabel={t(Resources.Common.Cancel)}
        title={t(Actions.DeleteConfirm.Title)}
        subtitle={t(Actions.DeleteConfirm.Text, { name })}
        primaryAction={
          <Button variant="contained" loading={deleteGoalMutation.isPending} onClick={deleteGoal}>
            {t(Resources.Common.Delete)}
          </Button>
        }
      />
    </Flex>
  );
};
